import { useEffect, useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";

// Pixally cross-promo card. Source of truth is the sibling app's component; the
// only local touches are the explicit Azeret Mono stack on the two mono labels
// (this app's `font-mono` is JetBrains Mono), the .js extension, and the `logo`
// switch below (the client side's site look, site-look.css).
const MONO = "'Azeret Mono', ui-monospace, monospace";

// Pixally ships Jan 1 — count down to the next one so this never goes stale.
function daysUntilLaunch() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (today.getMonth() === 0 && today.getDate() === 1) return 0;
  const target = new Date(today.getFullYear() + 1, 0, 1);
  return Math.round((target - today) / 86400000);
}

// Kept to the card's margins so they read as light, not as typos in the copy.
const SPARKS = [
  { top: "12%", left: "90%", delay: "0s" },
  { top: "93%", left: "3%", delay: "1.1s" },   // was 86%/7%: the extra copy line put it on the J of "Join"
  { top: "52%", left: "94%", delay: "2.3s" },
  { top: "30%", left: "4%", delay: "3.1s" },
];

// Pixally's own white wordmark (pixally.com's, for dark grounds), at the 20px the name was set in. The sheen
// laid over it is masked to the logo's letters, so the light runs through the name as it did through the text.
const LOGO = { src: '/pixally-logo-white-40.png', srcSet: '/pixally-logo-white-40.png 2x, /pixally-logo-white-60.png 3x' };
const LOGO_MASK = {
  WebkitMaskImage: 'url(/pixally-logo-white-60.png)', maskImage: 'url(/pixally-logo-white-60.png)',
  WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
  WebkitMaskPosition: 'left center', maskPosition: 'left center',
};

// `logo`: the client side's site look shows Pixally's real logo; staff keep the set text.
export default function PixallyAd({ logo = false }) {
  const [days, setDays] = useState(daysUntilLaunch);
  useEffect(() => {
    const tick = setInterval(() => setDays(daysUntilLaunch()), 3600000);
    return () => clearInterval(tick);
  }, []);
  return (
    <a
      data-testid="pixally-ad"
      href="https://pixally.com"
      target="_blank"
      rel="noopener noreferrer"
      className="pixally-ad group mt-auto"
      title="Pixally — join the waitlist"
    >
      <div className="pixally-inner px-3.5 py-3">
        {SPARKS.map((s, i) => (
          <span key={i} className="pixally-spark" style={{ top: s.top, left: s.left, animationDelay: s.delay }} />
        ))}
        <div className="relative flex items-center gap-1.5">
          <span className="pixally-dot" />
          <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#5ba89d]" style={{ fontFamily: MONO }}>
            {days === 0 ? "Launching today" : "Launches Jan 1"}
          </span>
          <Sparkles className="ml-auto h-3 w-3 text-[#5ba89d]/40 transition-colors group-hover:text-[#5ba89d]" />
        </div>
        {logo ? (
          <h3 className="relative mt-2 h-5" aria-label="Pixally">
            <img {...LOGO} alt="" className="block h-5 w-auto" />
            <span aria-hidden="true" className="pixally-logo-sheen" style={LOGO_MASK} />
          </h3>
        ) : (
          <h3
            className="pixally-wordmark relative mt-2 text-[20px] font-bold leading-none"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Pixally
          </h3>
        )}
        <p className="relative mt-1.5 text-[11px] leading-snug text-[#a1a1aa]">
          The all-in-one CRM for wedding videographers.
        </p>
        <p className="relative mt-1 text-[10px] italic leading-snug text-[#a1a1aa]/90">
          3 years in the making.
        </p>
        <div className="relative mt-3 flex items-center justify-between">
          <span className="flex items-center gap-1 text-[10px] font-semibold text-[#8fd8cc] transition-colors group-hover:text-white">
            Join the waitlist
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </span>
          {days > 0 && (
            <span className="font-mono text-[9px] tracking-wider text-[#52525b]" style={{ fontFamily: MONO }}>{days} days</span>
          )}
        </div>
      </div>
    </a>
  );
}
